import { useEffect, useState } from "react";
import { MainContainer } from "../Contents/Contents.element";
import PageHeader from "../../layout/PageHeader";
import SecondaryButton from "../../ui/SecondaryButton";
import "./playercomparison.css";
import IApiResponse from "../../types/IApiResponse";
import { postData } from "../../services/api";
import apiUrls from "../../config/apiUrls";
import handleApiResponse from "../../utils/handleApiResponse";
import { useLocation, useParams } from "react-router-dom";
import { IPlayerComparisonData, SortableKeys } from "../../types/IPlayerComparison";
import PlayerComparisonTableContent from "./PlayerComparisonTableContent";
import { useLoading } from "../../contexts/LoadingContext";
import { PlotComparison } from "./PlotComparison";
import { getPlotFilterDefaultKey, IPlotFilter, PlotFilters } from "./ScattrPlotUtils";
import { useNotification } from "../../contexts/NotificationContext";
import LabelText from "../../ui/LabelText";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useSidebar } from "../../contexts/SidebarContext";
import { TrackerComparison } from "./TrackerComparison";
import PWNotification from "../PWNotification";
import { useAuth } from "../../contexts/AuthContext";

interface playercomparisonToolprops {
    playerIDS? : number[]
    FromSortListPage?: boolean
}

const PlayerComparisionTool: React.FC<playercomparisonToolprops> = ({ playerIDS, FromSortListPage}) => {
  const { startLoading, stopLoading, loading } = useLoading();
  const { userId } = useParams<{ userId: string }>();
  const { userData} = useAuth();
  const location = useLocation();
  const { showNotification, hideNotification, displayNotification, setDisplayNotification, notificationContent, setNotificationContent } =
    useNotification();
  const { isOpen, toggleSubSidebar, subMenuOpen, setActiveItemId, toggleDrawer, drawerState, isBlurred, setIsBlurred } = useSidebar();
  const { playerIds, isFromSortList, benchMarkPlayerId } = location.state || [];
  const [activeContent, setActiveContent] = useState("table");
  const [playerComparisonTableData, setPlayerComparisonTableData] = useState<IPlayerComparisonData[]>([]);
  const [sortKey, setSortKey] = useState<SortableKeys>("attackContribution");

  const [plotFilters, setPlotFilters] = useState<IPlotFilter>({
    up: getPlotFilterDefaultKey(sortKey),
    across: null,
    dotSize: null,
  });
  useEffect(() => {
    setPlotFilters({
      up: getPlotFilterDefaultKey(sortKey),
      across: plotFilters?.across,
      dotSize: plotFilters?.dotSize,
    });
  }, [sortKey]);
  const [selectedPlotFilterOption, setSelectedPlotFilterOption] = useState<"up" | "across" | "dotSize" | undefined>(undefined);
  const [acrossSortDirection, setAcrossSortDirection] = useState<"inc" | "dec">("inc");
  const [upSortDirection, setUpSortDirection] = useState<"asc" | "desc">("desc");

  useEffect(() => {
    if (FromSortListPage && playerIDS?.length === 0) {
      setDisplayNotification(true);
      setNotificationContent(
        <div className="notification">
          <p className="sortlist-notofication-content">
            <InfoOutlinedIcon className="icon" />
            <LabelText className="squad-chart-tooltip notification-content">Short list currently empty.</LabelText>
          </p>
        </div>
      );
    }
  }, []);
    //call playerComparison api from compariosn tool :
  const fetchPlayerComparisondata = async (filteredPlayerIds: number[]) => {
    startLoading();
    const requestParameters = {
      playerId: filteredPlayerIds,
      userId: userData
    };
    const response: IApiResponse<any> = await postData(apiUrls.PlayerComparison, requestParameters);
    handleApiResponse(
      response,
      (playerComparisonData) => {
        stopLoading();
        if (response.data.length > 0) {
          setPlayerComparisonTableData(playerComparisonData);
        } else {
          if (FromSortListPage) {
            setDisplayNotification(true);
            setNotificationContent(
              <div className="notification">
                <p className="sortlist-notofication-content">
                  <InfoOutlinedIcon className="icon" />
                  <LabelText className="squad-chart-tooltip notification-content">Short list currently empty.</LabelText>
                </p>
              </div>
            );
          }
        }
      },
      (error) => {
        stopLoading();
        console.error("Error fetching player comparison table data:", error);
      }
    );
  };

  useEffect(() => {
    const storedRemovePlayerIds: number[] = JSON.parse(localStorage.getItem("removedIds") || "[]");
    if (playerIds && playerIds.length > 0) {
      const filteredPlayerIds = playerIds.filter((id: number) => !storedRemovePlayerIds.includes(id));
      if (activeContent === "table") {
        fetchPlayerComparisondata(storedRemovePlayerIds.length > 0 ? filteredPlayerIds : playerIds);
      }
    } else {
      if (playerIDS && playerIDS.length > 0) {
        const filteredPlayerIds = playerIDS?.filter((id: number) => !storedRemovePlayerIds.includes(id));
        if (activeContent === "table") {
          fetchPlayerComparisondata(storedRemovePlayerIds.length > 0 ? filteredPlayerIds : playerIDS);
        }
      }
    }
  }, [activeContent]);

  return (
    <MainContainer isBlurred={isBlurred!} className={"main-container player-detail-page"}>
      <PageHeader pageTitle={isFromSortList || FromSortListPage ? "Player shortlist" : "Player Comparison"} />
      <div className="player-comparison-content player-comparison-content-without-scroll">
        <div className="comparison-tool-btns">
          <div className="comparison-tool-btns-view">
            <SecondaryButton
              iconType="table"
              label="Table"
              isSelected={activeContent === "table"}
              onClick={() => setActiveContent("table")}
            />
            <SecondaryButton iconType="plot" label="Plot" isSelected={activeContent === "plot"} onClick={() => setActiveContent("plot")} />
            <SecondaryButton
              iconType="tracker"
              label="Tracker"
              isSelected={activeContent === "tracker"}
              onClick={() => setActiveContent("tracker")}
            />
            {/* <SecondaryButton
                            iconType='pitch'
                            label='System fit'
                            isSelected={activeContent === 'systemfit'}
                            onClick={() => setActiveContent('systemfit')} /> */}
          </div>
          {activeContent === "plot" && (
            <PlotFilters
              acrossSortDirection={acrossSortDirection}
              setAcrossSortDirection={setAcrossSortDirection}
              upSortDirection={upSortDirection}
              setUpSortDirection={setUpSortDirection}
              setPlotFilters={setPlotFilters}
              plotFilters={plotFilters}
              selectedPlotFilterOption={selectedPlotFilterOption}
              setSelectedPlotFilterOption={setSelectedPlotFilterOption}
            />
          )}
        </div>
        <div className="comparison-tool-content">
          {activeContent === "table" && (
            <div className="content-table">
              <PlayerComparisonTableContent
                sortKey={sortKey}
                setSortKey={setSortKey}
                tableData={playerComparisonTableData}
                isFormSortList={isFromSortList ? isFromSortList : FromSortListPage}
                benchmarkID={benchMarkPlayerId}
              />
            </div>
          )}
          {activeContent === "plot" && (
            <div className="content-plot">
              <PlotComparison
                benchMarkPlayerId={benchMarkPlayerId}
                upSortDirection={upSortDirection}
                acrossSortDirection={acrossSortDirection}
                playerComparisonData={playerComparisonTableData}
                plotFilters={plotFilters}
                isFormSortList={isFromSortList ? isFromSortList : FromSortListPage}
              />
            </div>
          )}
          {activeContent === "tracker" && (
            <div className="content-tracker">
              <TrackerComparison 
                playerComparisonData={playerComparisonTableData}  
                isFormSortList={isFromSortList ? isFromSortList : FromSortListPage}
            />
            </div>
          )}
          {activeContent === "systemfit" && (
            <div className="content-pitch">
              <h1>System Fit Content</h1>
            </div>
          )}
        </div>
      </div>
      {/* {displayNotification &&
                <PWNotification message={notificationContent} setIsNotification={() => setDisplayNotification(false)} isDisplayCloseIcon={false}
                    className='player-filter-notification comparison-notification' />} */}
    </MainContainer>
  );
};

export default PlayerComparisionTool;
